// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import useStyles from "./new-request-dialog.style";
import CloseIcon from "@iconify/icons-mdi/close";
import { InlineIcon } from "@iconify/react";
import {
  MyTimeRequestTypeInterface,
  MyTimeSelectMyTimeType,
} from "my-time/components";
import { MY_TIME_CREATE_REQUEST } from "my-time/graphql";
import {
  parseDateTime,
  timeConvert,
  useProtectedPeriodsData,
  useWorkTimeLimits,
  validateNewRequest,
} from "my-time/utils";
import { OcTimeDateCounter, OcTimePicker } from "oc/components";
import { useOcProperty, useOcSnackbar } from "oc/hooks";

interface MyTimeNewRequestDialogProps {
  action: string;
  contractCode: string;
  data: any;
  handleClose: () => void;
  open: boolean;
  list: any[];
  isMobile: boolean;
  selectedRowData: any;
}

export const MyTimeNewRequestDialog = ({
  action,
  contractCode,
  data,
  handleClose,
  open,
  list,
  isMobile,
  selectedRowData,
}: MyTimeNewRequestDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles({ isMobile });
  const { setOpenMessage } = useOcSnackbar();

  const [selectedMyTimeType, setSelectedMyTimeType] = useState<string>("");
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<{ state: boolean; messages: string[] }>({
    state: false,
    messages: [],
  });
  const [timePeriod, setTimePeriod] = useState(0);
  const [timePeriodInfo, setTimePeriodInfo] = useState("");

  useEffect(() => {
    if (!data?.myTimeType) return;
    if (selectedMyTimeType) return;

    const sameDay = (d1: any, d2: any) =>
      moment(d1).format("YYYY-MM-DD") === moment(d2).format("YYYY-MM-DD");

    const isExist = list?.find(
      (item) => sameDay(item.date, data?.date) && item?.myTimeType === "OVERTIME"
    );
    const isWorkingTime = list?.find(
      (item) =>
        sameDay(item?.date, data?.date) && item?.myTimeType === "WORKING_TIME"
    );

    if (isExist && !isWorkingTime) {
      setSelectedMyTimeType("STAND_BY_CALL");
    } else {
      if (data?.myTimeType !== "WORKING_TIME") {
        setSelectedMyTimeType("OVERTIME");
      }
    }
  }, [data?.myTimeType, data?.date, list, selectedMyTimeType]);

  const effectiveMyTimeType = selectedMyTimeType || data?.myTimeType;
  const label = effectiveMyTimeType
    ? t(`MY_TIME_${effectiveMyTimeType}`)
    : t(`MY_TIME_${data?.myTimeType}`);

  const { protectedTimeRanges } = useProtectedPeriodsData({
    list,
    data: { ...data, myTimeType: selectedMyTimeType },
  });

  useOcProperty({
    resource: "myTime",
    key: "timePeriod",
    onCompleted: ({ value }) => {
      const newTimePeriod = Number.parseInt(value);
      setTimePeriod(newTimePeriod);
      if (newTimePeriod === 10) {
        setTimePeriodInfo(t("MY_TIME_10_MINUTES_TIME_PERIOD_INFO"));
      } else if (newTimePeriod === 30) {
        setTimePeriodInfo(t("MY_TIME_30_MINUTES_TIME_PERIOD_INFO"));
      } else {
        setTimePeriodInfo("");
      }
    },
  });

  const {
    workTimeData,
    weeklyWorktimeLimit,
    weeklyVoluntaryOvertimeLimit,
    timeSummary,
  } = useWorkTimeLimits({
    contractCode,
    date: data?.date,
    startTime: startTime ? startTime.toString() : "",
    endTime: endTime ? endTime.toString() : "",
    myTimeType: effectiveMyTimeType,
  });

  useEffect(() => {
    if (!open) return;
    if (startTime || endTime) return;

    if (action === "edit") {
      if (data?.startTime) setStartTime(new Date(data.startTime));
      if (data?.endTime) setEndTime(new Date(data.endTime));
      return;
    }

    if (action === "add" && (data?.myTimeType === "OVERTIME" || data?.myTimeType === "PERSONAL_LEAVE")) {
      // Don't set startTime if effectiveMyTimeType is PERSONAL_LEAVE
      if (effectiveMyTimeType === "PERSONAL_LEAVE") {
        return;
      }

      // Check if there's HOMEOFFICE on this day to use its endTime
      const sameDay = (d1: any, d2: any) =>
        moment(d1).format("YYYY-MM-DD") === moment(d2).format("YYYY-MM-DD");

      const isThereHomeOffice = list?.find(
        (item) =>
          sameDay(item?.date, data?.date) && item?.myTimeType === "HOMEOFFICE"
      );

      if (isThereHomeOffice) {
        setStartTime(new Date(isThereHomeOffice.endTime));
        return;
      }

      if (
        isMobile &&
        selectedRowData?.dayType !== "REST_DAY" &&
        selectedRowData?.dayType !== "PAID_HOLLIDAY" &&
        selectedRowData?.endTime
      ) {
        setStartTime(new Date(selectedRowData.endTime));
        return;
      }
      if (data?.startTime) setStartTime(new Date(data.startTime));
    }
  }, [
    open,
    action,
    data?.myTimeType,
    data?.startTime,
    data?.endTime,
    data?.date,
    isMobile,
    selectedRowData?.dayType,
    selectedRowData?.endTime,
    startTime,
    endTime,
    effectiveMyTimeType,
    list,
  ]);

  // Clear startTime when user manually switches to PERSONAL_LEAVE
  useEffect(() => {
    if (!open) return;
    if (!selectedMyTimeType) return; // Only when user selected something manually
    if (selectedMyTimeType !== "PERSONAL_LEAVE") return;

    // Clear time fields
    setStartTime(null);
    setEndTime(null);
  }, [selectedMyTimeType, open]);

  const handleStartTimeChange = (newValue: string | Date) => {
    if (error.state) setError({ state: false, messages: [] });
    if (moment(newValue).isValid()) {
      const time = moment(newValue).format("HH:mm");
      const date = moment(data?.date).format("YYYY-MM-DD");
      const value = moment(`${date} ${time}`).toDate();
      setStartTime(value);
    }
  };

  const handleEndTimeChange = (newValue: string | Date) => {
    if (error.state) setError({ state: false, messages: [] });
    if (moment(newValue).isValid()) {
      const time = moment(newValue).format("HH:mm");
      const date = moment(data?.date).format("YYYY-MM-DD");
      let value = moment(`${date} ${time}`).toDate();
      if (startTime && value < startTime) {
        value = moment(value).add(1, "days").toDate();
      }
      setEndTime(value);
    }
  };

  const handleMessage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const [createRequest] = useMutation(MY_TIME_CREATE_REQUEST, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_TIME_REQUEST_CREATE_SUCCEEDED"),
      });
      handleClear();
      handleClose();
    },
    onError: () => {
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_REQUEST_CREATE_FAILED"),
      });
    },
  });

  const handleClear = () => {
    setError({ state: false, messages: [] });
    setStartTime(null);
    setEndTime(null);
    setSelectedMyTimeType("");
    setMessage("");
  };

  const handleCancel = () => {
    handleClose();
    setError({ state: false, messages: [] });
    setSelectedMyTimeType("");
    setMessage("");
    setTimeout(() => {
      handleClear();
    }, 500);
  };

  const handleSave = () => {
    const createParams = workTimeData?.map((item: any) => {
      const startTime = parseDateTime(item?.startTime);
      const endTime = parseDateTime(item?.endTime);
      return {
        contractCode: contractCode,
        myTimeType: effectiveMyTimeType,
        hours: timeConvert(item?.workTime),
        state: "requested",
        timeDataTypeCode: item?.timeDataTypeCode,
        timeDataTypeName: item?.timeDataTypeName,
        startTime: startTime,
        endTime: endTime,
        message: message,
      };
    });

    const newData = { ...data, myTimeType: effectiveMyTimeType };

    let hasValidationError = false;
    if (startTime && endTime) {
      hasValidationError = validateNewRequest({
        requests: createParams,
        list,
        data: newData,
        protectedTimeRanges,
        timePeriod,
        weeklyWorktimeLimit,
        weeklyVoluntaryOvertimeLimit,
        error,
        setError,
        startTime,
        endTime,
      });
    }

    if (
      data?.myTimeType === "WORKING_TIME" &&
      data?.startTime &&
      data?.endTime &&
      startTime &&
      endTime &&
      moment(data.startTime).isSame(startTime) &&
      moment(data.endTime).isSame(endTime)
    ) {
      setError({ state: true, messages: [t("MY_TIME_DATA_NOT_CHANGED")] });
      return;
    }

    if (hasValidationError || error.messages.length > 0) return;
    createParams?.forEach((params: any) => {
      createRequest({ variables: params });
    });
  };

  const startTimeValue = startTime ? new Date(startTime) : null;
  const endTimeValue = endTime ? new Date(endTime) : null;
  const isSaveDisabled = !(startTime && endTime) || error.state;

  return (
    <Dialog
      fullScreen={isMobile ? true : false}
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick") return false;
        handleClose();
      }}
    >
      <Box padding="16px" width={isMobile ? "auto" : "460px"}>
        <Box
          style={{
            fontSize: "20px",
            letterSpacing: "0.15px",
            padding: "0px 16px 10px 16px",
            borderBottom: "2px solid #C6CACC",
          }}
        >
          <Typography className="oc-card-h6-title" variant="h6" color="#5E6366">
            {label}
          </Typography>
          {isMobile && (
            <Box position="absolute" right="16px" top="16px">
              <InlineIcon
                icon={CloseIcon}
                width="24px"
                onClick={handleCancel}
                style={{ cursor: "pointer" }}
              />
            </Box>
          )}
        </Box>
        <Box height="32px" />
        <Box style={{ display: "flex", justifyContent: "center" }}>
          <Typography variant="h6" style={{ color: "#CC0033" }}>
            {new Date(data?.date)
              .toLocaleDateString("hu-HU", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
              .replace(",", "")}
          </Typography>
        </Box>
        <MyTimeSelectMyTimeType
          data={data}
          selectedMyTimeType={selectedMyTimeType}
          onSelectMyTimeType={setSelectedMyTimeType}
        />
        <Box height="16px" />
        <Box className={classes.centerContainer}>
          <Box className={classes.pickerContainer}>
            <OcTimePicker
              label={t("OC_START_TIME")}
              id="start-time-input"
              value={startTimeValue}
              onChange={handleStartTimeChange}
              isMobile={isMobile}
              timePeriod={timePeriod}
            />
            <OcTimePicker
              label={t("OC_END_TIME")}
              id="end-time-input"
              value={endTimeValue}
              onChange={handleEndTimeChange}
              isMobile={isMobile}
              timePeriod={timePeriod}
            />
          </Box>
          {isMobile && <Box width="26px" />}
          <OcTimeDateCounter
            time={timeSummary}
            counterLabel={t("MY_TIME_WORKING_TIME")}
          />
        </Box>
        {isMobile && <Box height="26px" />}
        {timePeriodInfo && (
          <Box marginTop="-30px" marginBottom="24px">
            <Typography variant="body2">{timePeriodInfo}</Typography>
          </Box>
        )}
        <Collapse in={workTimeData?.length > 0}>
          {workTimeData?.map((item, index) => {
            return (
              <Box key={`type-interface-${index}}`}>
                <MyTimeRequestTypeInterface
                  data={item}
                  myTimeType={effectiveMyTimeType}
                />
                <Box height="16px" />
              </Box>
            );
          })}
        </Collapse>
        <Box height="16px" />
        <TextField
          id="my-time-message-input"
          label={t("OC_MESSAGE")}
          multiline
          value={message}
          onChange={handleMessage}
          fullWidth
          variant="outlined"
          helperText={
            message.length === 0 ? t("OC_OPTIONAL") : `${message.length}/255`
          }
          style={{ height: "auto" }}
          inputProps={{ maxLength: 255 }}
          FormHelperTextProps={
            message.length === 0
              ? { style: { textAlign: "left" } }
              : { style: { textAlign: "right" } }
          }
        />
        <Collapse in={error.state}>
          <Box
            style={{
              width: "100%",
              color: "#cc0033",
              textAlign: "center",
              paddingTop: "18px",
            }}
          >
            {error.messages.map((message, index) => {
              return (
                <Typography
                  key={`error-message-${index}`}
                  className={classes.errorMessage}
                  variant="body2"
                  color="#CC0033"
                >
                  {t(message)}
                </Typography>
              );
            })}
          </Box>
        </Collapse>
        <Box height="32px" />
        <Box className={classes.line} />
        <Button
          id="save-button"
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {t("OC_SAVE")}
        </Button>
        <Box className={classes.divider} />
        <Button
          id="cancel-button"
          text={t("OC_CANCEL")}
          variant="text"
          color="secondary"
          fullWidth
          onClick={handleCancel}
        >
          {t("OC_CANCEL")}
        </Button>
        <Box height="16px" />
      </Box>
    </Dialog>
  );
};

export default MyTimeNewRequestDialog;
